import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, api } from "@/lib/api.ts";
import type { CashSessionOut } from "@/types/api.ts";

export const cashSessionKeys = {
  all: ["cashSessions"] as const,
  current: () => [...cashSessionKeys.all, "current"] as const,
  detail: (id: string) => [...cashSessionKeys.all, "detail", id] as const,
};

/**
 * GET /cash-sessions/current. Si no hay sesión abierta el backend devuelve 404 —
 * lo traducimos a `null` para que el caller diferencie "no hay caja" vs error real.
 */
export function useCurrentCashSession() {
  return useQuery({
    queryKey: cashSessionKeys.current(),
    queryFn: async (): Promise<CashSessionOut | null> => {
      try {
        return await api.get<CashSessionOut>("/cash-sessions/current");
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    staleTime: 5_000,
  });
}

interface OpenSessionVars {
  opened_amount: string;
  branch_id?: string;
  notes?: string;
}

export function useOpenCashSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: OpenSessionVars) => api.post<CashSessionOut>("/cash-sessions/open", vars),
    onSuccess: (data) => {
      queryClient.setQueryData(cashSessionKeys.current(), data);
    },
  });
}

interface CloseSessionVars {
  session_id: string;
  declared_amount: string;
  notes?: string;
}

export function useCloseCashSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ session_id, ...body }: CloseSessionVars) =>
      api.post<CashSessionOut>(`/cash-sessions/${session_id}/close`, body),
    onSuccess: () => {
      queryClient.setQueryData(cashSessionKeys.current(), null);
      queryClient.invalidateQueries({ queryKey: cashSessionKeys.all });
    },
  });
}
